<template>
  <div class="grid-x grid-margin-x">
    <div
      v-if="isMobile"
      class="cell title-mobile"
      :class="isSpacingExperimentEnabled ? 'padding-bottom-xs' : 'padding-bottom-s'"
    >
      <ProductEngagementMcom
        v-if="bestSeller"
        class="hide-for-medium"
        is-best-seller
      />
      <ProductTitle />
      <MarketPlaceTitle
        v-if="marketPlaceItemEnabled && isMcom"
      />
      <ReviewRating />
    </div>

    <div class="cell relative medium-6 large-6 xlarge-7">
      <Media />
      <ProductEngagementMcom
        v-if="productHasEngagementPill"
        is-pill
      />
    </div>

    <div class="cell medium-6 large-5 large-offset-1 xlarge-5 xlarge-offset-0">
      <div
        v-if="!isMobile"
        class="title"
      >
        <ProductEngagementMcom
          v-if="bestSeller"
          class="show-for-medium"
          is-best-seller
        />
        <ProductTitle />
        <MarketPlaceTitle
          v-if="marketPlaceItemEnabled && isMcom"
        />
        <ReviewRating />
      </div>
      <ProductTraits>
        <template #pzo>
          <Pzo v-if="showPzo && !isProductPerksEnabled" />
        </template>
        <template #shop-more>
          <ShopMoreFacade v-if="isMcom && isShopMoreStandardEnabled" />
        </template>
        <template #ppc>
          <Ppc v-if="!(isMcom && isProductPerksEnabled)" />
        </template>
      </ProductTraits>

      <template v-if="isBcom && !dnfPlacementEnabled">
        <Availability
          v-if="isAvailabilityEnabled"
          class="margin-bottom-m"
          data-testid="availability"
        />
        <AvailabilityBigTicket
          v-if="isBigTicketItemFlag"
          class="margin-bottom-s"
        />
        <OnDisplay
          v-if="isBigTicketItemFlag && showOnDisplay"
        />
      </template>

      <AutoReplenishment v-if="autoReplenishmentEnabled" />
      <CTAButtons
        v-if="callToActionButtons"
        :class="[productHasEngagementPill ? 'margin-bottom-m medium-margin-bottom-z' : 'margin-bottom-m']"
      />

      <template v-if="isBcom && dnfPlacementEnabled">
        <Availability
          v-if="isAvailabilityEnabled"
          class="margin-bottom-m"
          data-testid="availability"
        />
        <AvailabilityBigTicket
          v-if="isBigTicketItemFlag"
          class="margin-bottom-s"
        />
        <OnDisplay
          v-if="isBigTicketItemFlag && showOnDisplay"
        />
      </template>

      <ShippingAndReturnsBadgeFacade
        v-if="killswitches.isShippingAndReturnsBadgeEnabled"
        class="margin-bottom-m"
      />
      <LoyalList
        v-if="isLoyalList"
        class="margin-bottom-m"
      />
      <ProductEngagementMcom
        v-if="productHasEngagementPill"
        class="show-for-medium"
      />
      <template v-if="isMcom">
        <Availability
          v-if="isAvailabilityEnabled"
          class="margin-bottom-m"
          data-testid="availability"
        />
        <AvailabilityBigTicket
          v-if="isBigTicketItemFlag"
          class="margin-bottom-m"
        />
        <OnDisplay
          v-if="isBigTicketItemFlag && showOnDisplay"
        />
      </template>
      <BTZipCodeSlideout v-if="isBigTicketItemFlag" />
      <ProductPerks v-if="perksDesktopTreatment" />
      <ProsZoneAVertical
        v-if="!isMobile"
        data-testid="pros-section"
        class="margin-top-m pros-45"
      />
      <ProductPerks
        v-if="perksDesktopTreatment2"
        class="margin-top-m"
      />
      <LazyLoadWhenInViewport>
        <FrequentlyBoughtTogether v-if="prosFBT" />
      </LazyLoadWhenInViewport>
      <LazyLoadWhenInViewport>
        <SponsoredItemTop
          v-if="showSponsoredItemTop"
          :enable-click-tags-for-s-m-products="killswitches.enableClickTagsForSMProducts"
          :is-simplification-mcom-on="killswitches.priceSimplificationEnabled"
        />
      </LazyLoadWhenInViewport>
    </div>

    <div
      class="cell large-12"
      data-testid="product-details-section"
    >
      <ProductDetailsFacade :class="{'margin-top-xl': (!isMobile && !showSponsoredItemTop) || prosFBT}" />
    </div>
    <ProductPerks
      v-if="perksMobileTreatment"
      class="margin-top-m"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { SponsoredItemTop } from '@common-vue/site-monetization';
import ProductPerks from '@modules/productPerks/components/ProductPerks';
import OnDisplay from '@components/common/OnDisplay';
import ProductTitle from '@components/ProductCard/ProductTitle.common';
import ReviewRating from '@components/ReviewRating';
import MarketPlaceTitle from '@components/marketPlace/MarketPlaceTitle';
import Media from '@components/common/Media/Media';
import ProductEngagementMcom from '@components/ProductEngagement.mcom';
import CTAButtons from '@components/ctaButtons/CTAButtons';
import FrequentlyBoughtTogether from '@components/pros/FrequentlyBoughtTogether';
import ProductTraits from '@components/ProductCard/ProductTraits';
import LoyalList from '@components/LoyalList.bcom';
import LazyLoadWhenInViewport from '@components/common/LazyLoadWhenInViewport';
import { ProductDetailsFacade } from '@modules/productDetails';
import ProsZoneAVertical from '@components/pros/ProsZoneAVertical';
import { ShippingAndReturnsBadgeFacade } from '@modules/shippingAndRetunsBadge';
import { ShopMoreFacade } from '@modules/shop-more';

import {
  Availability, AvailabilityBigTicket, useAvailability, BTZipCodeSlideout,
} from '@modules/availability';
import { useFeatureEligibility, useProductFlags, useKillswitches } from '@modules/featureControl';
import { Ppc } from '@modules/ppc';
import { Pzo, usePzo } from '@modules/pzo';
import { AutoReplenishment, useAutoReplenishmentProvide } from '@modules/auto-replenishment';

import { useBrand } from '@composables/useBrand';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useExperiments } from '@composables/useExperiments';

const { killswitches } = useKillswitches();
const { isBigTicketItemFlag } = useProductFlags();
const {
  productHasEngagementPill, bestSeller,
  callToActionButtons, prosFBT, autoReplenishmentEnabled,
  showSponsoredItemTop,
  isLoyalList,
  marketPlaceItemEnabled,
  isProductPerksEnabled,
} = useFeatureEligibility();
const { isAvailabilityEnabled } = useAvailability();
const { isMcom, isBcom } = useBrand();
const { isMobile } = useDeviceType();

const { showPzo } = usePzo();
const {
  experimentFlags: {
    isProductPerksTreatment,
    isProductPerksTreatment2,
    isSpacingExperimentEnabled,
    dnfPlacementEnabled,
    isShopMoreStandardEnabled,
  },
} = useExperiments();

const showOnDisplay = computed(() => (
  (!isMcom && killswitches.isBcomBigTicketSeeItInStoreEnabled)
    || (isMcom && killswitches.isMcomBigTicketSeeItInStoreEnabled)
));

const perksDesktopTreatment = computed(() => isProductPerksEnabled && isProductPerksTreatment && !isMobile.value);
const perksDesktopTreatment2 = computed(() => isProductPerksEnabled && isProductPerksTreatment2 && !isMobile.value);
const perksMobileTreatment = computed(() => isProductPerksEnabled && isProductPerksTreatment && isMobile.value);
/**
 * Auto replenishment
 * Using the useAutoReplenishmentProvide composable to provide the auto replenishment state
 */
useAutoReplenishmentProvide(autoReplenishmentEnabled);
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@include title;
</style>
